import { useState, type ReactNode } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { MEMORY_TYPES, MEMORY_SCOPES, type AgentWizardData } from '@/schemas/agent-wizard'
import { useProjectStore } from '@/stores/project-store'
import { agentService } from '@/services/agent'
import {
  Database,
  Clock,
  RefreshCw,
  Repeat,
  Settings,
  ToggleLeft,
  Trash2,
  Brain,
  Layers,
  FileText,
  Calendar,
} from 'lucide-react'

interface StepAdvancedProps {
  editMode?: boolean
  agentId?: string
}

export function StepAdvanced({ editMode = false, agentId }: StepAdvancedProps) {
  const { t } = useTranslation('agents')
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<AgentWizardData>()

  const selectedProject = useProjectStore((state) => state.selectedProject)

  const isActive = watch('is_active')
  const isTemplate = watch('is_template')
  const memoryEnabled = watch('memory_enabled')
  const memoryType = watch('memory_type')

  // Clear memory dialog state
  const [showClearMemoryDialog, setShowClearMemoryDialog] = useState(false)
  const [isClearingMemory, setIsClearingMemory] = useState(false)
  const [clearMemoryError, setClearMemoryError] = useState<string | null>(null)

  // Handle clear memory
  const handleClearMemory = async () => {
    if (!selectedProject?.id || !agentId) return

    setIsClearingMemory(true)
    setClearMemoryError(null)

    try {
      await agentService.clearMemory(selectedProject.id, agentId)
      setShowClearMemoryDialog(false)
    } catch (err) {
      setClearMemoryError(t('wizard.advanced.memory.clearMemoryError'))
    } finally {
      setIsClearingMemory(false)
    }
  }

  return (
    <div className="space-y-8">
      {/* Memory Configuration Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-gray-700/50">
          <Database className="h-5 w-5 text-indigo-400" />
          <h3 className="text-lg font-medium text-white">{t('wizard.advanced.memory.title')}</h3>
        </div>

        <LinhaDeInterruptor
          id="memoria-ativa"
          icone={<Brain className="h-5 w-5 flex-shrink-0 text-indigo-400" />}
          titulo={t('wizard.advanced.memory.enable')}
          descricao={
            memoryEnabled
              ? t('wizard.advanced.memory.enabledDesc')
              : t('wizard.advanced.memory.disabledDesc')
          }
          marcado={memoryEnabled}
          aoMudar={(valor) => setValue('memory_enabled', valor)}
        />

        {/* Memory Configuration (conditional on memoryEnabled) */}
        {memoryEnabled && (
          <div className="space-y-4 pl-4 border-l-2 border-indigo-500/30">
            {/* Memory Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Layers className="h-4 w-4" />
                  {t('wizard.advanced.memory.type')}
                </Label>
                <Controller
                  name="memory_type"
                  control={control}
                  render={({ field }) => (
                    <Select
                      onValueChange={field.onChange}
                      value={field.value ?? 'short_term'}
                    >
                      <SelectTrigger
                        className={cn(
                          'bg-white/5 border-gray-700/50 text-white',
                          'focus:border-indigo-500 focus:ring-indigo-500/20'
                        )}
                      >
                        <SelectValue placeholder={t('wizard.advanced.memory.selectType')} />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-700">
                        {MEMORY_TYPES.map((type) => (
                          <SelectItem
                            key={type.value}
                            value={type.value}
                            className="text-gray-200 focus:bg-indigo-500/20 focus:text-white"
                          >
                            <div className="flex flex-col">
                              <span>{type.label}</span>
                              <span className="text-xs text-gray-500">
                                {type.description}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              {/* Memory Scope */}
              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Layers className="h-4 w-4" />
                  {t('wizard.advanced.memory.scope')}
                </Label>
                <Controller
                  name="memory_scope"
                  control={control}
                  render={({ field }) => (
                    <Select
                      onValueChange={field.onChange}
                      value={field.value ?? 'conversation'}
                    >
                      <SelectTrigger
                        className={cn(
                          'bg-white/5 border-gray-700/50 text-white',
                          'focus:border-indigo-500 focus:ring-indigo-500/20'
                        )}
                      >
                        <SelectValue placeholder={t('wizard.advanced.memory.selectScope')} />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-700">
                        {MEMORY_SCOPES.map((scope) => (
                          <SelectItem
                            key={scope.value}
                            value={scope.value}
                            className="text-gray-200 focus:bg-indigo-500/20 focus:text-white"
                          >
                            <div className="flex flex-col">
                              <span>{scope.label}</span>
                              <span className="text-xs text-gray-500">
                                {scope.description}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>

            {/* Context Window and Retention Days */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="memory_context_window" className="text-gray-300 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  {t('wizard.advanced.memory.contextWindow')}
                </Label>
                <Controller
                  name="memory_context_window"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="memory_context_window"
                      type="number"
                      min={1}
                      max={100}
                      placeholder="20"
                      value={field.value ?? 20}
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 20)
                      }
                      className={cn(
                        'bg-white/5 border-gray-700/50 text-white',
                        'focus:border-indigo-500 focus:ring-indigo-500/20'
                      )}
                    />
                  )}
                />
                <p className="text-xs text-gray-500">
                  {t('wizard.advanced.memory.contextWindowInfo')}
                </p>
              </div>

              {memoryType === 'long_term' && (
                <div className="space-y-2">
                  <Label htmlFor="memory_retention_days" className="text-gray-300 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {t('wizard.advanced.memory.retentionDays')}
                  </Label>
                  <Controller
                    name="memory_retention_days"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="memory_retention_days"
                        type="number"
                        min={1}
                        placeholder="30"
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? parseInt(e.target.value) : null
                          )
                        }
                        className={cn(
                          'bg-white/5 border-gray-700/50 text-white',
                          'focus:border-indigo-500 focus:ring-indigo-500/20'
                        )}
                      />
                    )}
                  />
                  <p className="text-xs text-gray-500">
                    {t('wizard.advanced.memory.retentionDaysInfo')}
                  </p>
                </div>
              )}
            </div>

            <LinhaDeInterruptor
              id="resumo-automatico"
              compacta
              tom="emerald"
              tamanho="sm"
              titulo={t('wizard.advanced.memory.autoSummarize')}
              descricao={t('wizard.advanced.memory.autoSummarizeDesc')}
              marcado={Boolean(watch('memory_auto_summarize'))}
              aoMudar={(valor) => setValue('memory_auto_summarize', valor)}
            />

            {/* Clear Memory Button (only in edit mode) */}
            {editMode && agentId && (
              <div className="pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowClearMemoryDialog(true)}
                  className="text-red-400 border-red-500/30 hover:bg-red-500/10 hover:border-red-500/50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('wizard.advanced.memory.clearMemory')}
                </Button>
                <p className="text-xs text-gray-500 mt-1">
                  {t('wizard.advanced.memory.clearMemoryDesc')}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Execution Settings Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-gray-700/50">
          <Settings className="h-5 w-5 text-indigo-400" />
          <h3 className="text-lg font-medium text-white">{t('wizard.advanced.execution.title')}</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Timeout */}
          <div className="space-y-2">
            <Label
              htmlFor="timeout"
              className="text-gray-300 flex items-center gap-2"
            >
              <Clock className="h-4 w-4" />
              {t('wizard.advanced.execution.timeout')}
            </Label>
            <Controller
              name="timeout"
              control={control}
              render={({ field }) => (
                <Input
                  id="timeout"
                  type="number"
                  min={1}
                  max={600}
                  value={field.value ?? 60}
                  onChange={(e) => field.onChange(parseInt(e.target.value) || 60)}
                  className={cn(
                    'bg-white/5 border-gray-700/50 text-white',
                    'focus:border-indigo-500 focus:ring-indigo-500/20',
                    errors.timeout && 'border-red-500'
                  )}
                />
              )}
            />
            {errors.timeout && (
              <p className="text-sm text-red-400">{errors.timeout.message}</p>
            )}
          </div>

          {/* Max Retries */}
          <div className="space-y-2">
            <Label
              htmlFor="max_retries"
              className="text-gray-300 flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              {t('wizard.advanced.execution.maxRetries')}
            </Label>
            <Controller
              name="max_retries"
              control={control}
              render={({ field }) => (
                <Input
                  id="max_retries"
                  type="number"
                  min={0}
                  max={10}
                  value={field.value ?? 3}
                  onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                  className={cn(
                    'bg-white/5 border-gray-700/50 text-white',
                    'focus:border-indigo-500 focus:ring-indigo-500/20',
                    errors.max_retries && 'border-red-500'
                  )}
                />
              )}
            />
            {errors.max_retries && (
              <p className="text-sm text-red-400">{errors.max_retries.message}</p>
            )}
          </div>

          {/* Max Iterations */}
          <div className="space-y-2">
            <Label
              htmlFor="max_iterations"
              className="text-gray-300 flex items-center gap-2"
            >
              <Repeat className="h-4 w-4" />
              {t('wizard.advanced.execution.maxIterations')}
            </Label>
            <Controller
              name="max_iterations"
              control={control}
              render={({ field }) => (
                <Input
                  id="max_iterations"
                  type="number"
                  min={1}
                  max={100}
                  value={field.value ?? 10}
                  onChange={(e) => field.onChange(parseInt(e.target.value) || 10)}
                  className={cn(
                    'bg-white/5 border-gray-700/50 text-white',
                    'focus:border-indigo-500 focus:ring-indigo-500/20',
                    errors.max_iterations && 'border-red-500'
                  )}
                />
              )}
            />
            {errors.max_iterations && (
              <p className="text-sm text-red-400">
                {errors.max_iterations.message}
              </p>
            )}
          </div>
        </div>

        <p className="text-xs text-gray-500">
          {t('wizard.advanced.execution.info')}
        </p>
      </div>

      {/* Status Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-gray-700/50">
          <ToggleLeft className="h-5 w-5 text-indigo-400" />
          <h3 className="text-lg font-medium text-white">{t('wizard.advanced.status.title')}</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <LinhaDeInterruptor
            id="agente-publicado"
            tom="emerald"
            titulo={t('wizard.advanced.status.publish')}
            descricao={
              isActive
                ? t('wizard.advanced.status.publishedDesc')
                : t('wizard.advanced.status.draftDesc')
            }
            marcado={isActive}
            aoMudar={(valor) => setValue('is_active', valor)}
          />

          <LinhaDeInterruptor
            id="agente-modelo"
            titulo={t('wizard.advanced.status.template')}
            descricao={
              isTemplate
                ? t('wizard.advanced.status.templateEnabledDesc')
                : t('wizard.advanced.status.templateDisabledDesc')
            }
            marcado={isTemplate}
            aoMudar={(valor) => setValue('is_template', valor)}
          />
        </div>
      </div>

      {/* Clear Memory Confirmation Dialog */}
      <ConfirmationDialog
        open={showClearMemoryDialog}
        onOpenChange={setShowClearMemoryDialog}
        title={t('wizard.advanced.memory.clearMemoryTitle')}
        description={
          <>
            {t('wizard.advanced.memory.clearMemoryMessage')}
          </>
        }
        confirmLabel={t('wizard.advanced.memory.clearMemoryConfirm')}
        confirmingLabel={t('wizard.advanced.memory.clearMemoryConfirming')}
        confirmIcon={<Trash2 className="h-4 w-4 mr-2" />}
        confirmVariant="action-red"
        isProcessing={isClearingMemory}
        error={clearMemoryError}
        onConfirm={handleClearMemory}
      />
    </div>
  )
}

/* ─── A linha de interruptor ─── */

/**
 * **O CARTÃO ERA UM `<button>` COM UM DESENHO DE INTERRUPTOR DENTRO.** Quem lê
 * com leitor de tela ouvia "botão", não "interruptor", e não ouvia se estava
 * ligado. Aqui o controle é o `Switch` de verdade, e o cartão é um `<label>`
 * ligado a ele: clicar em qualquer parte continua alternando, o nome acessível
 * vem do próprio título, e o estado é anunciado.
 *
 * O `min-w-0` no bloco de texto é o que impede a volta do defeito da ISSUE-192
 * pelo outro lado: sem ele, uma descrição longa empurra o interruptor e o
 * `flex-shrink-0` dele vira a única defesa.
 */
interface LinhaDeInterruptorProps {
  id: string
  titulo: string
  descricao: string
  marcado: boolean
  aoMudar: (valor: boolean) => void
  icone?: ReactNode
  tom?: 'indigo' | 'emerald'
  tamanho?: 'md' | 'sm'
  compacta?: boolean
}

function LinhaDeInterruptor({
  id,
  titulo,
  descricao,
  marcado,
  aoMudar,
  icone,
  tom = 'indigo',
  tamanho = 'md',
  compacta = false,
}: LinhaDeInterruptorProps) {
  return (
    <label
      htmlFor={id}
      className={cn(
        'flex w-full cursor-pointer items-center justify-between gap-3 rounded-xl border bg-white/5 transition-all duration-200',
        compacta ? 'p-3' : 'p-4',
        marcado
          ? tom === 'emerald'
            ? 'border-emerald-500 bg-emerald-500/10'
            : 'border-indigo-500 bg-indigo-500/10'
          : 'border-gray-700/50 hover:border-gray-600'
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        {icone}
        <div className="min-w-0">
          <p className={cn('font-medium text-white', compacta && 'text-sm')}>
            {titulo}
          </p>
          <p className={cn('text-gray-400', compacta ? 'text-xs' : 'text-sm')}>
            {descricao}
          </p>
        </div>
      </div>
      <Switch
        id={id}
        checked={marcado}
        onCheckedChange={aoMudar}
        tom={tom}
        tamanho={tamanho}
      />
    </label>
  )
}
